<script setup lang="ts">
import { EntityNameSpan } from '@precon-tools-suite-by-shared/shared-names-ui';
import { computed } from 'vue';
import { usePreconUserProfilesOptions } from '../composables/usePreconUserProfilesOptions';
import Skeleton from 'primevue/skeleton';

type Team = {
    id: string;
    name?: { full?: string | null; short?: string | null } | null;
};

const props = defineProps<{
    teams: Array<Team> | null | undefined;
    currentTeamId: string | null | undefined;
    disabled?: boolean;
    expectedTeamCount?: number;
}>();

const emits = defineEmits<{
    (e: 'team:click', team: Team, event: MouseEvent): void;
}>();

const options = usePreconUserProfilesOptions();

const selectedTeamId = defineModel<string | null>();

const expectedTeamCount = computed(
    () => props.expectedTeamCount ?? options.value.expectedTeamCount
);

const teamIconColorClass = computed(() =>
    props.disabled ? 'text-500' : 'text-primary'
);

function getTeamIconClass(team: Team): string {
    if (team.id === selectedTeamId.value) {
        if (team.id === props.currentTeamId) {
            return 'pi pi-circle-fill';
        } else {
            return 'pi pi-spinner pi-spin';
        }
    } else {
        return 'pi pi-circle';
    }
}

function teamClickHandler(team: Team, event: MouseEvent) {
    emits('team:click', team, event);
    selectedTeamId.value = team.id;
}
</script>

<template>
    <ul :class="['current-team-selector', { disabled: props.disabled }]">
        <template v-if="props.teams">
            <li
                v-for="team in props.teams"
                :key="team.id"
                @click="($event) => teamClickHandler(team, $event)"
            >
                <i
                    class="mr-2"
                    :class="[teamIconColorClass, getTeamIconClass(team)]"
                />
                <EntityNameSpan :name="team.name" />
            </li>
        </template>
        <template v-else>
            <li v-for="i in expectedTeamCount" :key="`team-placeholder-${i}`">
                <i class="mr-2 text-500 pi pi-circle" />
                <Skeleton />
            </li>
        </template>
    </ul>
</template>

<style lang="scss">
.current-team-selector {
    list-style-type: none;
    padding-inline-start: 0;

    > li {
        padding: 0.5rem 15px;
        display: flex;
        align-items: center;

        &:hover {
            background-color: var(--primary-lighter-color);
        }
    }

    &.disabled {
        > li {
            &:hover {
                background-color: none;
            }
        }
    }
}
</style>
